import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { PDFDownloadLink } from '@react-pdf/renderer'
import { Trans, useTranslation } from 'react-i18next'
import classNames from 'classnames'
import PropTypes, { object } from 'prop-types'

import { SUPPORT_ARTICLE_URLS } from 'constants'

import InfoBox from 'components/Shared/InfoBox'
import { numberWithThousandsSeparators, snakeCaseToPascalCase, treatAsProductEntry } from 'utils/helpers'
import { Accordion, Card } from 'react-bootstrap'
import moment from 'moment'
import EventStatsPDF from '../EventStatsPDF'
import TipDialogueBox from 'components/Shared/TipDialogueBox'
import EventStatsOverview from './EventStatsOverview'
import PlatformToggle, { useHasMultiplePlatforms } from 'components/Shared/PlatformToggle'
import { SETTINGS_PLATFORMS_ANCHOR } from 'domain/platform/permissionErrorTarget'
import AccordionCard from 'components/Shared/AccordionCard'
import LoadingProgressBar from 'components/Shared/LoadingProgressBar'
import { isCampaignRunning } from 'api/models'
import { initStateObject, objectReducer, LOADING, OVERWRITE, FAIL } from 'reducers/default'
import { fetchCustomConversionById } from 'api/integrations'
import eventEvaluationApi from 'domain/eventEvaluation/api'
import { EventEvaluationErrorCode } from 'domain/eventEvaluation/schemas'
import { useAttributionModel } from 'domain/events/attributionModel/EventAttributionModelContextProvider'
import { ATTRIBUTION_MODELS } from 'domain/events/attributionModel/eventAttributionModelReducer'
import EventCard from './EventCard'
import useCustomTranslation from 'hooks/useCustomTranslation'
import { hasPartialServiceAccessTier } from 'hooks/useHasPermission'
import Notice from 'components/Shared/Notice'
import BestPerformingAds from './BestPerformingAds'
import InsightsBreakdown from './InsightsBreakdown'

// For prisma-format entries (category 'product' or 'prisma') the
// event/ticket-worded stat
// labels swap to their product/item counterparts; actual events keep them.
// Prisma-tier partners keep campaign wording via their translation namespace.
const PRODUCT_STATS_KEYS = {
  'Dashboard.eventSeriesStats.tickets.title': 'Dashboard.eventSeriesStats.product.itemsTitle',
  'Dashboard.eventSeriesStats.purchases.description': 'Dashboard.eventSeriesStats.product.purchasesDescription',
  'Dashboard.eventSeriesStats.adSpendPerTicket.description': 'Dashboard.eventSeriesStats.product.cpoDescription',
  'Dashboard.eventSeriesStats.revenue.description': 'Dashboard.eventSeriesStats.product.revenueDescription',
  'Dashboard.eventSeriesStats.roi.description': 'Dashboard.eventSeriesStats.product.roiDescription',
  'Dashboard.eventSeriesStats.singleEvent': 'Dashboard.eventSeriesStats.product.singleProduct',
  'Dashboard.eventSeriesStats.eventSeries': 'Dashboard.eventSeriesStats.product.productSeries',
  'Dashboard.eventSeriesStats.includedEvents': 'Dashboard.eventSeriesStats.product.includedProducts',
}

// The META enum value means "platform-reported attribution" (whichever
// platform), so its note is keyed `platform`, not `meta`.
const NO_RESULTS_NOTE_KEYS = {
  [ATTRIBUTION_MODELS.fd]: 'fd',
  [ATTRIBUTION_MODELS.meta]: 'platform',
  [ATTRIBUTION_MODELS.external]: 'external',
  [ATTRIBUTION_MODELS.dynamic]: 'dynamic',
}

// Notes whose advice differs per platform have one key per platform toggle
// option; the rest are platform-agnostic.
const PLATFORM_SCOPED_NO_RESULTS_NOTES = ['platform', 'dynamic']

const EventStatistics = ({ event, eventSeries, campaigns = [] }) => {
  const { t, i18n } = useTranslation()
  const [activeKey, setActiveKey] = useState(null)
  const user = useSelector((store) => store.user)
  const [adCreatives, setAdCreatives] = useState({
    totalAds: 0,
    adSuggestions: { regular: [], reels: [] },
  })
  const [platform, setPlatform] = useState('all')
  const hasMultiplePlatforms = useHasMultiplePlatforms()
  const [isAttributionAccordionOpen, setIsAttributionAccordionOpen] = useState(true)

  const handlePlatformChange = (newPlatform) => {
    setPlatform(newPlatform)
    setIsAttributionAccordionOpen(true)
  }
  const [campaignResults, dispatchCampaignResults] = useReducer(objectReducer, initStateObject)
  const [deliveryInsights, setDeliveryInsights] = useState(null)
  // Scoped to the Attribution card's own `platform` toggle (not the Delivery
  // card's separate one) so the PDF export matches whatever this component's
  // toggle is set to when Save is clicked, same scope as eventStats/
  // eventSeriesStats below.
  const [pdfInsights, setPdfInsights] = useState({})
  const runningCampaigns = useMemo(
    () => campaigns.filter((campaign) => isCampaignRunning(campaign)),
    [campaigns],
  )
  const includedEventsRef = useRef(null)
  const openIncludedEvents = new URL(window.location.href).searchParams.get('open_included_events')
  const { attributionModel } = useAttributionModel()

  const [{ content: campaignConversion }, dispatchCampaignConversionAction] = useReducer(
    objectReducer,
    {
      ...initStateObject,
    },
  )
  const currency = useMemo(() => localStorage.getItem('userCurrency'), [])

  const eventSeriesSupportLink = SUPPORT_ARTICLE_URLS?.eventSeries?.[i18n.language] || SUPPORT_ARTICLE_URLS?.eventSeries?.en

  const hasExternalAttributionModel = useMemo(
    () => attributionModel === ATTRIBUTION_MODELS.external,
    [attributionModel],
  )
  const { getTranslation } = useCustomTranslation(event)
  // Product wording only for prisma-format entries; actual events keep their
  // event/ticket stat labels. Prisma-tier partners keep campaign wording.
  const isProductContext =
    !hasPartialServiceAccessTier() && treatAsProductEntry(event.category)
  const statsKey = (key) => (isProductContext && PRODUCT_STATS_KEYS[key]) || key
  const renderStatValue = (value) =>
    campaignResults.loading ? <LoadingProgressBar visible className="stats-loader" /> : value

  useEffect(() => {
    if (!event.id) return
    let cancelled = false

    dispatchCampaignResults({ type: LOADING })
    eventEvaluationApi.fetchCampaignResultsAsync(event.id, platform).then((res) => {
      if (cancelled) return
      if (res.error) {
        dispatchCampaignResults({
          type: OVERWRITE,
          payload: { error_code: res.error.code, message: res.error.message },
        })
        dispatchCampaignResults({ type: FAIL, payload: res.error.message })
        return
      }
      dispatchCampaignResults({ type: OVERWRITE, payload: res.data })
    })

    return () => {
      cancelled = true
    }
  }, [event.id, platform, attributionModel])

  useEffect(() => {
    if (!event.id) return
    let cancelled = false

    eventEvaluationApi.fetchInsightsAsync(event.id, platform).then((res) => {
      if (cancelled) return
      setPdfInsights(res.data ?? {})
    })

    return () => {
      cancelled = true
    }
  }, [event.id, platform])

  const isShortCampaign = useMemo(() => {
    return (
      attributionModel !== ATTRIBUTION_MODELS.meta &&
      campaignResults.error &&
      campaignResults.content?.error_code === EventEvaluationErrorCode.marketingTooShortToEvaluate
    )
  }, [campaignResults, attributionModel])

  // FD Live: show the note about collecting data in the first days if backend returns code
  const isFdLiveCollecting = useMemo(() => {
    if (attributionModel !== ATTRIBUTION_MODELS.fd) return false
    const errorCode = campaignResults?.content?.error_code

    return errorCode === EventEvaluationErrorCode.evaluationUnavailableYet
  }, [attributionModel, campaignResults])

  const {
    eventResults: eventStats,
    seriesResults: eventSeriesStats,
    platformAttribution,
  } = campaignResults.content
  // platform_attribution only carries a key for the requested platform(s) —
  // 'all' isn't itself a key, and a lone google request has no meta entry to
  // fall back on, so resolve explicitly rather than assuming `.meta`.
  const resolvedAttributionPlatform =
    platform === 'all' ? (platformAttribution?.meta ? 'meta' : 'google') : platform
  const platformAttributionEntry = platformAttribution?.[resolvedAttributionPlatform]
  const eval_name = platformAttributionEntry?.eval_name
  const ad_account_id = platformAttributionEntry?.ad_account_id

  useEffect(() => {
    // if eval_name doesn't include numerical characters, it means it's a custom event type
    if (!eval_name || !/\d/.test(eval_name)) {
      return
    }

    // Custom conversions are a Meta concept and the endpoint is Meta-only, so
    // the ids have to come from the Meta entry — the platform-reported model
    // also resolves to the Google entry when the card is showing Google.
    if (
      attributionModel === ATTRIBUTION_MODELS.meta &&
      resolvedAttributionPlatform === 'meta' &&
      ad_account_id &&
      eval_name
    ) {
      fetchCustomConversionById(dispatchCampaignConversionAction, ad_account_id, eval_name)
    }
  }, [
    ad_account_id,
    eval_name,
    dispatchCampaignConversionAction,
    attributionModel,
    resolvedAttributionPlatform,
  ])

  // Without Google access the toggle never renders and `platform` stays 'all',
  // which would otherwise name Google in the advice.
  const notePlatform = hasMultiplePlatforms ? platform : 'meta'
  const noResultsNoteName = NO_RESULTS_NOTE_KEYS[attributionModel] ?? 'fd'
  const noResultsNoteKey = PLATFORM_SCOPED_NO_RESULTS_NOTES.includes(noResultsNoteName)
    ? `Dashboard.eventSeriesStats.noResultsNotes.${noResultsNoteName}.${notePlatform}`
    : `Dashboard.eventSeriesStats.noResultsNotes.${noResultsNoteName}`

  const ticketsTitle =
    attributionModel !== ATTRIBUTION_MODELS.meta
      ? 'Dashboard.eventSeriesStats.tickets.title'
      : campaignConversion?.name
        ? 'Dashboard.eventSeriesStats.conversion'
        : 'Dashboard.eventSeriesStats.purchases.title'

  const attributionModelBasedEventSeriesStats = useMemo(
    () => (attributionModel === ATTRIBUTION_MODELS.fd ? eventSeriesStats : {}),
    [attributionModel, eventSeriesStats],
  )
  const attributionModelBasedEventStats = useMemo(
    () => (attributionModel === ATTRIBUTION_MODELS.external ? {} : eventStats),
    [attributionModel, eventStats],
  )
  const filteredEventSeries =
    attributionModel === ATTRIBUTION_MODELS.meta
      ? []
      : (eventSeries?.series || []).filter((el) => el.id !== event.id)
  const numOfTCs = runningCampaigns.length

  openIncludedEvents && includedEventsRef.current && includedEventsRef.current.scrollIntoView(true)

  const hasResults =
    attributionModelBasedEventStats?.totalTransactions > 0 ||
    attributionModelBasedEventSeriesStats?.totalTransactions > 0 ||
    attributionModelBasedEventStats?.cpo > 0 ||
    attributionModelBasedEventSeriesStats?.cpo > 0 ||
    attributionModelBasedEventStats?.totalRevenue > 0 ||
    attributionModelBasedEventSeriesStats?.totalRevenue > 0 ||
    attributionModelBasedEventStats?.roas > 0 ||
    attributionModelBasedEventSeriesStats?.roas > 0

  return (
    <div className="d-flex flex-column gap-4 mt-4">
      <EventStatsOverview
        currency={currency}
        eventId={event.id}
        runningCampaigns={runningCampaigns}
        setAdCreatives={setAdCreatives}
        onInsightsChange={setDeliveryInsights}
      />

      <BestPerformingAds eventId={event.id} />
      
      <div className="best-performing-ads event-series-stats">
        <AccordionCard
          isOpen={isAttributionAccordionOpen}
          onToggle={setIsAttributionAccordionOpen}
          title={
            <div className="header-with-platform-toggle">
              <InfoBox withAnchor={false}>
                <h2 className="card-title-default d-inline-block mb-0 mt-1">
                  {t('Dashboard.eventSeriesStats.attribution')}
                </h2>
              </InfoBox>

              {attributionModel !== ATTRIBUTION_MODELS.fd && <PlatformToggle platform={platform} setPlatform={handlePlatformChange} disabled={campaignResults.loading}/>}
            </div>
          }
        >
          <div className="event-series-stats">
            {campaignResults.loading || hasResults ? (
              <>
                <div
                  className={`attribution-content ${isShortCampaign || hasExternalAttributionModel ? 'event-series-stats_disabled-card' : ''}`}
                >
                  <div>
                    <InfoBox
                      title={t(statsKey(ticketsTitle))}
                      body={
                        <Trans
                          i18nKey={getTranslation(
                            statsKey('Dashboard.eventSeriesStats.purchases.description'),
                          )}
                          components={{
                            a: (
                              <a
                                href={eventSeriesSupportLink}
                                className="infobox-tooltip__link"
                                rel="noopener noreferrer"
                                target="_blank"
                              >
                                  Find Answers
                              </a>
                            ),
                            p: (
                              <p>
                                  Evaluation for event series will only be possible for FD
                                  attribution model
                              </p>
                            ),
                          }}
                        />
                      }
                      delay={300}
                    >
                      <h2 className="card-title-default d-inline-block mb-0 p-0">
                        {t(statsKey(ticketsTitle))}
                      </h2>
                    </InfoBox>

                    <div className="event-series-stats_data">
                      <div className="event event_single">
                        <span className="bold">
                          {renderStatValue(
                            numberWithThousandsSeparators(
                              attributionModelBasedEventStats?.totalTransactions,
                            ),
                          )}
                        </span>
                        <span className="ellipsis">
                          {attributionModel === ATTRIBUTION_MODELS.meta &&
                            !!campaignConversion?.name
                            ? campaignConversion.name
                            : eval_name
                              ? snakeCaseToPascalCase(eval_name)
                              : getTranslation(statsKey('Dashboard.eventSeriesStats.singleEvent'))}
                        </span>
                      </div>

                      <div className="event event_series">
                        <span className="bold">
                          {renderStatValue(
                            numberWithThousandsSeparators(
                              attributionModelBasedEventSeriesStats?.totalTransactions,
                            ),
                          )}
                        </span>
                        <span>{getTranslation(statsKey('Dashboard.eventSeriesStats.eventSeries'))}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <InfoBox
                      content="Dashboard.eventSeriesStats.adSpendPerTicket"
                      body={
                        <Trans
                          i18nKey={getTranslation(
                            statsKey('Dashboard.eventSeriesStats.adSpendPerTicket.description'),
                          )}
                          components={{
                            a: (
                              <a
                                href={eventSeriesSupportLink}
                                className="infobox-tooltip__link"
                                rel="noopener noreferrer"
                                target="_blank"
                              >
                                  Find Answers
                              </a>
                            ),
                            p: (
                              <p>
                                  Evaluation for event series will only be possible for FD
                                  attribution model
                              </p>
                            ),
                          }}
                        />
                      }
                      delay={300}
                    >
                      <h2 className="card-title-default d-inline-block mb-0 p-0">
                        {t('Dashboard.eventSeriesStats.adSpendPerTicket.title')}
                      </h2>
                    </InfoBox>
                    <div className="event-series-stats_data">
                      <div className="event event_single">
                        <span className="bold">
                          {renderStatValue(
                            numberWithThousandsSeparators(attributionModelBasedEventStats?.cpo, {
                              currency,
                              maxFractionDigits: 0,
                            }),
                          )}
                        </span>
                        <span>{getTranslation(statsKey('Dashboard.eventSeriesStats.singleEvent'))}</span>
                      </div>
                      <div className="event event_series">
                        <span className="bold">
                          {renderStatValue(
                            numberWithThousandsSeparators(
                              attributionModelBasedEventSeriesStats?.cpo,
                              {
                                currency,
                                maxFractionDigits: 0,
                              },
                            ),
                          )}
                        </span>
                        <span>{getTranslation(statsKey('Dashboard.eventSeriesStats.eventSeries'))}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <InfoBox
                      title={t('Dashboard.eventSeriesStats.revenue.title')}
                      body={
                        <Trans
                          i18nKey={getTranslation(
                            statsKey('Dashboard.eventSeriesStats.revenue.description'),
                          )}
                          components={{
                            a: (
                              <a
                                href={eventSeriesSupportLink}
                                className="infobox-tooltip__link"
                                rel="noopener noreferrer"
                                target="_blank"
                              >
                                  Find Answers
                              </a>
                            ),
                            p: (
                              <p>
                                  Evaluation for event series will only be possible for FD
                                  attribution model
                              </p>
                            ),
                          }}
                        />
                      }
                      delay={300}
                    >
                      <h2 className="card-title-default d-inline-block mb-0 p-0">
                        {t('Dashboard.eventSeriesStats.revenue.title')}
                      </h2>
                    </InfoBox>
                    <div className="event-series-stats_data">
                      <div className="event event_single">
                        <span className="bold">
                          {renderStatValue(
                            numberWithThousandsSeparators(
                              attributionModelBasedEventStats?.totalRevenue,
                              {
                                currency,
                                maxFractionDigits: 0,
                              },
                            ),
                          )}
                        </span>
                        <span>{getTranslation(statsKey('Dashboard.eventSeriesStats.singleEvent'))}</span>
                      </div>
                      <div className="event event_series">
                        <span className="bold">
                          {renderStatValue(
                            numberWithThousandsSeparators(
                              attributionModelBasedEventSeriesStats?.totalRevenue,
                              {
                                currency,
                                maxFractionDigits: 0,
                              },
                            ),
                          )}
                        </span>
                        <span>{getTranslation(statsKey('Dashboard.eventSeriesStats.eventSeries'))}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <InfoBox
                      content="Dashboard.eventSeriesStats.roi"
                      body={
                        <Trans
                          i18nKey={getTranslation(statsKey('Dashboard.eventSeriesStats.roi.description'))}
                          components={{
                            a: (
                              <a
                                href={eventSeriesSupportLink}
                                className="infobox-tooltip__link"
                                rel="noopener noreferrer"
                                target="_blank"
                              >
                                  Find Answers
                              </a>
                            ),
                            p: (
                              <p>
                                  Evaluation for event series will only be possible for FD
                                  attribution model
                              </p>
                            ),
                          }}
                        />
                      }
                      delay={300}
                    >
                      <h2 className="card-title-default d-inline-block mb-0 p-0">
                        {t('Dashboard.eventSeriesStats.roi.title')}
                      </h2>
                    </InfoBox>
                    <div className="event-series-stats_data">
                      <div className="event event_single">
                        <span className="bold">
                          {renderStatValue(
                            numberWithThousandsSeparators(attributionModelBasedEventStats?.roas, {
                              maxFractionDigits: 0,
                              suffix: '%',
                            }),
                          )}
                        </span>
                        <span>{getTranslation(statsKey('Dashboard.eventSeriesStats.singleEvent'))}</span>
                      </div>
                      <div className="event event_series">
                        <span className="bold">
                          {renderStatValue(
                            numberWithThousandsSeparators(
                              attributionModelBasedEventSeriesStats?.roas,
                              {
                                maxFractionDigits: 0,
                                suffix: '%',
                              },
                            ),
                          )}
                        </span>
                        <span>{getTranslation(statsKey('Dashboard.eventSeriesStats.eventSeries'))}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {(isShortCampaign || hasExternalAttributionModel) && (
                  <div className="event-series-stats disabled-overlay">
                    {hasExternalAttributionModel && (
                      <TipDialogueBox
                        title={t('common.note')}
                        text={t('Dashboard.eventSeriesStats.externalAttributionModelNotice')}
                        variant="danger"
                        showBorder
                        compact
                      />
                    )}
                    {isShortCampaign && !hasExternalAttributionModel && (
                      <TipDialogueBox
                        title={t('Dashboard.eventSeriesStats.runtimeAlert.title')}
                        text={t('Dashboard.eventSeriesStats.runtimeAlert.description')}
                        variant="danger"
                        showBorder
                        compact
                      />
                    )}
                  </div>
                )}
              </>
            ) : (
              <p className="no-results-note pb-0">
                {isFdLiveCollecting
                  ? t('Dashboard.eventSeriesStats.noResultsNotes.fd_period_error', {
                    days: 7,
                  })
                  : t(noResultsNoteKey)}
              </p>
            )}
          </div>
          {!!filteredEventSeries.length && (
            <div id="campaigns-container" className="row px-1">
              <div className={`col-12 event-series-stats_event-list ${isShortCampaign ? 'event-series-stats_disabled-card' : ''}`}>
                <Accordion ref={includedEventsRef} defaultActiveKey={openIncludedEvents ? '0' : null}>
                  <Card className="goals-card">
                    <Accordion.Toggle eventKey="0">
                      <Card.Header
                        as={Card.Header}
                        className={classNames({
                          'goals-card_header pt-3': true,
                          open: activeKey === '0' || openIncludedEvents,
                        })}
                        onClick={() =>
                          setActiveKey(
                            (activeKey && activeKey === '0') || openIncludedEvents ? null : '0',
                          )
                        }
                      >
                        <InfoBox withAnchor={false}>
                          {getTranslation(statsKey('Dashboard.eventSeriesStats.includedEvents'))}
                        </InfoBox>
                        <div className="align-self-center ml-auto">
                          <div
                            className={`accordion-arrow ${activeKey === '0' || openIncludedEvents ? 'open' : ''}`}
                          />
                        </div>
                      </Card.Header>
                    </Accordion.Toggle>

                    <Accordion.Collapse eventKey="0">
                      <Card.Body>
                        <div className="d-flex flex-column p-0">
                          {filteredEventSeries.map((el) => {
                            return <EventCard event={el} key={el.id} />
                          })}
                        </div>
                      </Card.Body>
                    </Accordion.Collapse>
                  </Card>
                </Accordion>
              </div>
            </div>
          )}
        </AccordionCard>
      </div>

      <InsightsBreakdown eventId={event.id} />
      
      <Notice
        isVisible={deliveryInsights?.userActionRequired}
        message={
          <p>
            <Trans
              i18nKey="Dashboard.eventSeriesStats.userActionRequiredAlert"
              components={{
                a: (
                  <a
                    href={SETTINGS_PLATFORMS_ANCHOR}
                    rel="noopener noreferrer"
                    target="_blank"
                    className="d-inline-block"
                    style={{ color: '#2F80ED' }}
                  >
                    assets page
                  </a>
                ),
              }}
            >
              Campaign insights are only partially filled out due to missing permissions for your
              connected ad platforms. Please reconnect the assets again on the{' '}
              <a href={SETTINGS_PLATFORMS_ANCHOR}>assets page</a>. The insights will update with the
              latest information within 24 hours.
            </Trans>
          </p>
        }
      />
      <div
        className={`event-series-stats_event-list ${isShortCampaign ? 'event-series-stats_disabled-card' : ''}`}
        id="campaigns-container"
      >
        {event.title && (
          <PDFDownloadLink
            document={
              <EventStatsPDF
                fbInsights={pdfInsights}
                eventStats={eventStats}
                eventSeriesStats={eventSeriesStats}
                eventSeries={filteredEventSeries}
                eventInfo={event}
                user={user}
                numOfTCs={numOfTCs}
                adCreatives={adCreatives}
              />
            }
            fileName={`${event.title}_campaign-stats_${moment().format('DD-MM-yyyy')}.pdf`}
          >
            {({ loading }) =>
              loading ? (
                t('Event.statistics.generatingDocument')
              ) : (
                <button style={{ width: '100%' }} className="bt s-38 bt-orange">
                  {t('Dashboard.eventSeriesStats.save')}
                </button>
              )
            }
          </PDFDownloadLink>
        )}
      </div>
    </div>
  )
}

EventStatistics.propTypes = {
  event: PropTypes.object.isRequired,
  eventSeries: PropTypes.object.isRequired,
  campaigns: PropTypes.arrayOf(object),
}

export default EventStatistics
